import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";

const rootEl = document.getElementById("root");

const fallbackHTML = rootEl ? rootEl.innerHTML : "";

function restoreFallbackOnFailure(reason: unknown) {
  console.error("[main] React mount failed, restoring fallback:", reason);
  if (rootEl && fallbackHTML) {
    try {
      rootEl.innerHTML = fallbackHTML;
    } catch (restoreErr) {
      console.error("[main] Fallback restore also failed:", restoreErr);
    }
  }
}

try {
  if (rootEl) {
    const root = createRoot(rootEl);
    try {
      root.render(
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      );
    } catch (renderErr) {
      restoreFallbackOnFailure(renderErr);
    }
  }
} catch (e) {
  restoreFallbackOnFailure(e);
}

window.addEventListener("error", (ev) => {
  if (rootEl && rootEl.childElementCount === 0) {
    restoreFallbackOnFailure(ev.error || ev.message);
  }
});

window.addEventListener("unhandledrejection", (ev) => {
  if (rootEl && rootEl.childElementCount === 0) {
    restoreFallbackOnFailure(ev.reason);
  }
});

// PWA: register the service worker once the page has finished loading.
// We only register in production builds to avoid interfering with Vite HMR.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  // When a brand-new SW takes control of the page (e.g. after a deploy that
  // bumps the SW version), the page is still running on whatever JS the old
  // SW served. Reload once so the user immediately sees fresh code instead
  // of being stuck on a stale UI until they manually refresh.
  // We only reload when an EXISTING controller is replaced — on a brand-new
  // first install (controller was null) there's nothing stale to escape, so
  // we skip the reload to avoid an unnecessary refresh on the user's first
  // visit.
  const hadController = !!navigator.serviceWorker.controller;
  let reloadingForNewSW = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) return;
    if (reloadingForNewSW) return;
    reloadingForNewSW = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // If a new SW is already waiting (e.g. installed during a previous
        // visit), tell it to activate now so users don't keep seeing stale
        // assets across navigations.
        if (reg.waiting) {
          reg.waiting.postMessage("SKIP_WAITING");
        }
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              installing.postMessage("SKIP_WAITING");
            }
          });
        });
      })
      .catch((err) => {
        console.warn("[pwa] service worker registration failed:", err);
      });
  });
} else if ("serviceWorker" in navigator && import.meta.env.DEV) {
  // In dev, proactively unregister any stale SW from a previous prod visit so
  // it doesn't intercept Vite HMR requests.
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => undefined);
}
